import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { cx } from '@pocketsurge/ui';
import type { PublicAdmin } from '@pocketsurge/shared/types';

const NAV = [
  { to: '/', label: 'Dashboard', icon: '📊' },
  { to: '/articles', label: 'Articles', icon: '📄' },
  { to: '/articles/new', label: 'New Article', icon: '✏️' },
  { to: '/upload', label: 'Bulk Upload', icon: '📦' },
  { to: '/blueprints', label: 'Blueprints', icon: '🧭' },
  { to: '/categories', label: 'Categories & Tags', icon: '🏷️' },
  { to: '/logs', label: 'Activity Logs', icon: '🕐' },
  { to: '/settings', label: 'Settings', icon: '⚙️' },
];

export function Layout({ admin }: { admin: PublicAdmin }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const logout = async () => {
    await api.post('/admin/logout');
    queryClient.clear();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen">
      <aside className="fixed inset-y-0 left-0 w-60 bg-slate-900 text-slate-300">
        <div className="flex h-16 items-center gap-2 border-b border-slate-800 px-5">
          <img
            src="/logo-light.webp"
            alt="PocketSurge"
            className="h-9 w-auto"
            width={397}
            height={159}
          />
          <span className="ml-1 rounded-full bg-slate-800 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-400">
            Admin
          </span>
        </div>

        <nav className="mt-4 space-y-1 px-3">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                cx(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition',
                  isActive ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                )
              }
            >
              <span>{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="absolute inset-x-0 bottom-0 border-t border-slate-800 p-4">
          <div className="mb-2 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-700 text-xs font-bold text-white">
              {admin.full_name
                .split(' ')
                .map((w) => w[0])
                .join('')
                .toUpperCase()
                .slice(0, 2)}
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-white">{admin.full_name}</div>
              <div className="text-xs text-slate-500">{admin.role}</div>
            </div>
          </div>
          <button
            onClick={logout}
            className="w-full rounded-lg bg-slate-800 px-3 py-2 text-sm font-medium text-slate-300 hover:bg-slate-700 hover:text-white"
          >
            Sign out
          </button>
        </div>
      </aside>

      <main className="ml-60 min-h-screen p-8">
        <Outlet />
      </main>
    </div>
  );
}
